import React, { useState } from 'react';
import _ from 'lodash';
import { Flex, TagWrapper } from 'components/base/layout-components';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import Text from 'components/base/Text';
import { Circle } from 'components/Shapes';
import Dropdown from 'components/base/Dropdown';
import { Tooltip } from '@material-ui/core';
import * as TrackingService from 'common/services/tracking.service';
import { ISSUE_STATES, ISSUE_STATES_DISPLAY_VALUES } from '../pulse.common';
import Link from '@material-ui/core/Link';
import CircularProgress from '@material-ui/core/CircularProgress';
import { TRACKING } from 'common/constants/constants';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import {
  ACCOUNT_TYPES_MAP,
  CAN_EDIT_ACCOUNT_TYPES
} from '@linearb/linjs-common';

const Wrapper = styled(Flex)`
  padding: 1.5rem;
  justify-content: space-between;
  position: relative;
`;
const LinkText = styled(Text)`
  :hover {
    color: ${getColor('chambray')};
  }
`;
const DropdownTagWrapper = styled.div`
  margin-right: 4rem;
  margin-bottom: 0.5rem;
`;

const ShadowWork = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 2.7rem 3rem 0 0;
  border-color: ${getColor('silverSand', 0.5)} transparent transparent
    transparent;
  left: -0.7rem;
  top: -0.3rem;
  position: absolute;
`;
const LoadableSelectedValue = styled(Flex)`
  padding-right: 0.8rem;
  padding-left: 0.4rem;
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
`;

const buildDropdownOptions = (tag, possibleStates = []) => {
  const statesByName = _.keyBy(possibleStates, 'state_name');
  // don't include the current state in the possible transitions
  delete statesByName[tag];
  return Object.values(statesByName).map(s => ({
    value: s,
    displayValue: <Text>{s.state_name}</Text>
  }));
};

const renderSelectedValue = (state, isLoading) => {
  return (
    <LoadableSelectedValue isLoading={isLoading} content='center'>
      <Text {...(isLoading ? { pr: '0.4rem' } : {})}>{state.state_name}</Text>
      {isLoading && <CircularProgress size={10} />}
    </LoadableSelectedValue>
  );
};

const IssueCol = ({
  name,
  tag,
  iconUrl,
  jiraLink,
  shadowWork,
  issueState,
  issueType,
  issueKey,
  storyPoints,
  possibleStates = [],
  isOpen = false,
  onOpenDropdown,
  onCloseDropdown,
  onSelectState,
  isLoading = false,
  withIssueStateDropdown = true,
  assignees
}) => {
  const colors =
    ISSUE_STATES_DISPLAY_VALUES[issueState || ISSUE_STATES.INITIATED].tagColors;
  const IssueTag = (
    <TagWrapper backgroundColor={colors.background} borderColor={colors.border}>
      <Text color={colors.text}>{tag}</Text>
    </TagWrapper>
  );
  const dropDownOptions = buildDropdownOptions(tag, possibleStates);
  const selectedValue = { state_name: tag };

  const [isHovering, setIsHovering] = useState(false);
  const showDropdown = isHovering || isLoading || isOpen;

  return (
    <Wrapper
      onMouseEnter={() => setIsHovering(withIssueStateDropdown)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {shadowWork && (
        <Tooltip
          placement='top'
          title='This issue is worked on, but was not selected for this sprint '
        >
          <ShadowWork />
        </Tooltip>
      )}
      <Flex mr='small'>
        <Tooltip title={issueType}>
          {iconUrl ? (
            <img src={iconUrl} className='size-4' alt='' />
          ) : (
            <span>
              {' '}
              <Circle size={1} />{' '}
            </span>
          )}
        </Tooltip>
        <Flex direction='column' ml='small'>
          <Link
            href={jiraLink}
            target='_blank'
            onClick={() =>
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.PULSE,
                click_type: TRACKING.CLICK.JIRA_REDIRECT
              })
            }
          >
            <LinkText
              cursor='pointer'
              color='shark'
              size='large'
              mb='0.8rem'
              multiLineEllipsisAfter={2}
            >
              {name}
            </LinkText>
          </Link>
          <Flex items='center' content='center' wrap='wrap'>
            <TagWrapper
              backgroundColor='white'
              borderColor='botticelli'
              mb='x-small'
            >
              <Text color='shark'>{issueKey}</Text>{' '}
            </TagWrapper>
            {storyPoints !== null && (
              <TagWrapper
                backgroundColor='white'
                borderColor='botticelli'
                mb='x-small'
              >
                {' '}
                <Text color='shuttleGray'>{storyPoints} Points</Text>{' '}
              </TagWrapper>
            )}
            {assignees && assignees.length
              ? assignees.map(assignee => (
                  // eslint-disable-next-line react/jsx-key
                  <TagWrapper
                    backgroundColor='white'
                    borderColor='botticelli'
                    mb='x-small'
                    data-testid='assignee-name'
                  >
                    <Text color='shuttleGray'>{assignee.name}</Text>{' '}
                  </TagWrapper>
                ))
              : null}

            {tag
              ? withPermissions({
                  WrappedComponent: showDropdown ? (
                    <Dropdown
                      dropdownStyles={{
                        marginBottom: '0.5rem',
                        marginRight: '3rem'
                      }}
                      inputProps={{ noPadding: true }}
                      options={dropDownOptions}
                      renderSelected={value =>
                        renderSelectedValue(value, isLoading)
                      }
                      selectedValue={selectedValue}
                      onChange={onSelectState}
                      width='fit-content'
                      open={isOpen}
                      onClose={onCloseDropdown}
                      onOpen={onOpenDropdown}
                      dataCy='pulse-ticket-actions-dropdown'
                    />
                  ) : (
                    <DropdownTagWrapper data-cy='pulse-ticket-status'>
                      {IssueTag}
                    </DropdownTagWrapper>
                  ),
                  requiredPermissions: CAN_EDIT_ACCOUNT_TYPES
                })
              : null}
            {tag
              ? withPermissions({
                  WrappedComponent: (
                    <DropdownTagWrapper>{IssueTag}</DropdownTagWrapper>
                  ),
                  requiredPermissions: [ACCOUNT_TYPES_MAP.Viewer]
                })
              : null}
          </Flex>
        </Flex>
      </Flex>
    </Wrapper>
  );
};

export default IssueCol;
